import { ActivityEnrollableTimesModal_ActivityFragment } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useSession } from "@outschool/ui-auth";
import {
  useIsMobile,
  useIsMounted,
  useScrollToElement,
} from "@outschool/ui-utils";
import React, { useRef, useState } from "react";

import {
  ChooseATimeOrClassScheduleRequestButtonProps,
  SectionCallToActionProps,
} from "../AvailableTimes/types";
import { TabValue } from "../PublicReviewsOfActivity";
import { AskAQuestionButtonMemo, MessageTeacherButtonMemo } from "./Buttons";
import {
  ActivityEnrollableTimesMemo,
  AvailableTimesMemo,
  BaseClassDetailsMainBodyProps,
  BecomeAMemberBannerMemo,
  DesktopClassDetailsMainBodyProps,
  MobileClassDetailsMainBodyProps,
  TeacherDetailsMemo,
  TeacherHeaderMemo,
  TeacherSectionMemo,
} from "./Components";
import DesktopClassDetailsMainBody from "./Desktop";
import MobileClassDetailsMainBody from "./Mobile";

interface ClassDetailsMainBodyProps extends BaseClassDetailsMainBodyProps {
  activityWithEnrollableTimes?: ActivityEnrollableTimesModal_ActivityFragment;
  isActivitySavedByCurrentUser?: boolean;
  isCrossSellVariant?: boolean;
  isOrganizationEnrollment: boolean;
  showAvailableTimes: boolean;
  SectionCallToAction: React.FC<SectionCallToActionProps>;
  ChooseATimeOrClassScheduleRequestButton: (
    props: ChooseATimeOrClassScheduleRequestButtonProps
  ) => JSX.Element;
  MessageModal: React.ElementType;
  FollowButton?: React.ElementType;
  TutorEnrollButton?: React.ElementType;
  MemberPlanDiscountTooltip?: React.JSX.Element;
  // scroll refs
  viewAvailabilityRef: React.RefObject<HTMLElement>;
  viewTheTeacherRef: React.RefObject<HTMLElement>;
  // event handlers
  onClickMoreLikeThis: () => void;
  onEnrollClick: (args: { startTime: Date; orgTeacherUid?: string }) => void;
  onClickViewTeacher?: (event: React.SyntheticEvent) => void;
  SeeScheduleButtonMemo?: React.ReactElement;
}

export default function ClassDetailsMainBody({
  activity,
  activityWithEnrollableTimes,
  leader,
  canEnroll,
  isCrossSellVariant,
  isOrganizationEnrollment,
  isActivitySavedByCurrentUser,
  isLoadingOneOnOneAvailabilities,
  hasOneOnOneAvailabilities,
  facebookAppId,
  showAvailableTimes,
  SectionCallToAction,
  ChooseATimeOrClassScheduleRequestButton,
  MessageModal,
  FollowButton,
  ReviewRespondButton,
  SeeScheduleButtonMemo,
  sectionsRef,
  viewAvailabilityRef,
  viewTheTeacherRef,
  onClickMoreLikeThis,
  onClickViewTeacher,
  onEnrollClick,
  isAvailabilityModalOpen = false,
  setIsAvailabilityModalOpen,
  TutorEnrollButton,
  MemberPlanDiscountTooltip,
}: ClassDetailsMainBodyProps) {
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\ClassDetailsMainBody\\index"
  );
  const { currentUser, isLoggedIn } = useSession();
  const isMobile = useIsMobile();
  const isMounted = useIsMounted();
  const scrollToElement = useScrollToElement();
  const [tabValue, setTabValue] = useState(TabValue.Class);
  const trackEvent = useTrackEvent();

  const refsContainer = {
    root: useRef<HTMLElement>(null),
    about: {
      header: useRef<HTMLElement>(null),
      whatsIncluded: useRef<HTMLElement>(null),
      classExperience: useRef<HTMLElement>(null),
    },
    learningOverview: {
      learningGoals: useRef<HTMLElement>(null),
      syllabus: useRef<HTMLElement>(null),
      otherDetails: useRef<HTMLElement>(null),
    },
    schedule: { sections: sectionsRef, availability: viewAvailabilityRef },
    teacher: {
      header: viewTheTeacherRef,
      expertise: useRef<HTMLElement>(null),
    },
    reviews: useRef<HTMLElement>(null),
  };

  const classNavTabs = [
    {
      label: t("About"),
      value: "about",
      ref: Object.values(refsContainer.about),
    },
    {
      label: t("Learning Overview"),
      value: "learning-overview",
      ref: Object.values(refsContainer.learningOverview),
    },
    {
      label: t("Schedule"),
      value: "schedule",
      ref: Object.values(refsContainer.schedule),
    },
    {
      label: t("Teacher"),
      value: "teacher",
      ref: Object.values(refsContainer.teacher),
    },
    { label: t("Reviews"), value: "reviews", ref: refsContainer.reviews },
  ];

  React.useEffect(() => {
    if (window.location.search.includes("teacher")) {
      scrollToElement(refsContainer.teacher.header.current);
    } else if (window.location.hash === "#availability") {
      scrollToElement(refsContainer.schedule.sections.current);
    }
  });

  const onClickViewReviews = React.useCallback(() => {
    setTabValue(TabValue.Leader); // switch to teacher reviews tab
    scrollToElement(refsContainer.reviews.current);
  }, [scrollToElement, refsContainer.reviews]);

  const ActivityEnrollableTimes = (
    <ActivityEnrollableTimesMemo
      activityWithEnrollableTimes={activityWithEnrollableTimes}
      leader={leader}
      onEnrollClick={onEnrollClick}
      ChooseATimeOrClassScheduleRequestButton={
        ChooseATimeOrClassScheduleRequestButton
      }
      activity={activity}
    />
  );

  const AskAQuestionButton = (
    <AskAQuestionButtonMemo
      activity={activity}
      currentUser={currentUser}
      isLoggedIn={isLoggedIn}
      leader={leader}
      trackEvent={trackEvent}
      MessageModal={MessageModal}
    />
  );

  const AvailableTimes = (
    <AvailableTimesMemo
      activity={activity}
      isMounted={isMounted}
      isCrossSellVariant={isCrossSellVariant}
      isOrganizationEnrollment={isOrganizationEnrollment}
      onClickMoreLikeThis={onClickMoreLikeThis}
      ChooseATimeOrClassScheduleRequestButton={
        ChooseATimeOrClassScheduleRequestButton
      }
      MessageModal={MessageModal}
      SectionCallToAction={SectionCallToAction}
    />
  );

  const BecomeAMemberBanner = <BecomeAMemberBannerMemo activity={activity} />;

  const MessageTeacherButton = (
    <MessageTeacherButtonMemo
      activity={activity}
      currentUser={currentUser}
      isLoggedIn={isLoggedIn}
      leader={leader}
      trackEvent={trackEvent}
      MessageModal={MessageModal}
    />
  );

  const TeacherDetails = (
    <TeacherDetailsMemo
      leader={leader}
      activity={activity}
      MessageTeacherButton={MessageTeacherButton}
      FollowButton={FollowButton}
      onClickViewReviews={onClickViewReviews}
      credentialsRef={refsContainer.teacher.expertise}
    />
  );

  const TeacherHeader = (
    <TeacherHeaderMemo
      isClub={activity.isClub}
      isMounted={isMounted}
      isOwnerSellerOrg={!!leader.ownerOfSellerOrg}
      teacherHeaderRef={refsContainer.teacher.header}
    />
  );

  const TeacherSection = (
    <TeacherSectionMemo
      TeacherDetails={TeacherDetails}
      TeacherHeader={TeacherHeader}
    />
  );

  const baseBodyProps: BaseClassDetailsMainBodyProps = {
    // props
    activity,
    canEnroll,
    classNavTabs,
    facebookAppId,
    hasOneOnOneAvailabilities,
    isLoadingOneOnOneAvailabilities,
    leader,
    onClickViewReviews,
    setTabValue,
    showAvailableTimes,
    tabValue,
    // refs
    sectionsRef,
    // memos & elements
    ActivityEnrollableTimes,
    AskAQuestionButton,
    AvailableTimes,
    MessageModal,
    ReviewRespondButton,
    TeacherSection,
  };

  const mobileBodyProps: MobileClassDetailsMainBodyProps = {
    ...baseBodyProps,
    // props
    isAvailabilityModalOpen,
    setIsAvailabilityModalOpen,
    // refs
    refsContainer,
    // memos & elements
    SeeScheduleButtonMemo,
    BecomeAMemberBanner,
  };

  const desktopBodyProps: DesktopClassDetailsMainBodyProps = {
    ...baseBodyProps,
    activityWithEnrollableTimes,
    // props
    isActivitySavedByCurrentUser,
    isOrganizationEnrollment,
    onClickMoreLikeThis,
    onClickViewTeacher,
    // refs
    refsContainer,
    viewAvailabilityRef,
    // memos & elements
    ChooseATimeOrClassScheduleRequestButton,
    SectionCallToAction,
    TutorEnrollButton,
    MemberPlanDiscountTooltip,
  };

  return isMobile ? (
    <MobileClassDetailsMainBody {...mobileBodyProps} />
  ) : (
    <DesktopClassDetailsMainBody {...desktopBodyProps} />
  );
}
